import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { CompareArrows, ExpandLess, ExpandMore, FilterList } from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { comparisonsApi, projectsApi } from '../../services/benchmarkApi';
import BenchmarkComparisonGridTab from './BenchmarkComparisonGridTab';
import { formatMoney, formatNumber, projectLabel } from './benchmarkFormat';

const RootDiv = styled('div')({
  flexGrow: 1,
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
});

const ContentDiv = styled('div')(({ theme }) => ({
  flexGrow: 1,
  overflow: 'auto',
  padding: theme.spacing(1.5),
}));

const StyledTabs = styled(Tabs)(({ theme }) => ({
  minHeight: 42,
  borderBottom: `1px solid ${theme.palette.divider}`,
  '& .MuiTabs-indicator': {
    height: 3,
    borderRadius: '3px 3px 0 0',
    backgroundColor: theme.palette.primary.main,
  },
}));

const StyledTab = styled(Tab)(({ theme }) => ({
  textTransform: 'none',
  fontWeight: 600,
  fontSize: '0.8125rem',
  minHeight: 42,
  px: 2,
  color: theme.palette.text.secondary,
  '&.Mui-selected': {
    color: theme.palette.primary.main,
  },
}));

const money = (v) => formatMoney(v);

const num = (v, digits = 2) => formatNumber(v, digits);

const pct = (v, digits = 1) =>
  v == null || v === '' ? '—' : `${Number(v) >= 0 ? '+' : ''}${Number(v).toFixed(digits)}%`;

const bandColor = (band) => {
  switch (band) {
    case 'Outlier':
      return 'error';
    case 'High':
      return 'warning';
    case 'Low':
      return 'info';
    default:
      return 'success';
  }
};

/** Under budget (negative) = good green; over = soft red. */
const varianceSx = (v) => {
  if (v == null || v === '' || Number.isNaN(Number(v))) return {};
  const n = Number(v);
  if (n < 0) return { color: '#2E7D32', fontWeight: 600 };
  if (n > 0) return { color: '#C62828', fontWeight: 600 };
  return { color: 'text.secondary', fontWeight: 500 };
};

const KPI_ACCENTS = {
  Selected: '#8B1538',
  Actual: '#1565C0',
  'Cost / m²': '#00897B',
  Percentile: '#2E7D32',
  Peers: '#546E7A',
  Outliers: '#EF6C00',
};

/**
 * Phase-2 Project Comparison — hotel-style analytical dashboard.
 */
const BenchmarkProjectComparison = () => {
  const [projects, setProjects] = useState([]);
  const [comparisons, setComparisons] = useState([]);
  const [comparisonName, setComparisonName] = useState('Hotel comparison');
  const [selectedProjectId, setSelectedProjectId] = useState('');
  const [peerIds, setPeerIds] = useState([]);
  const [activeComparisonId, setActiveComparisonId] = useState('');
  const [overview, setOverview] = useState(null);
  const [structures, setStructures] = useState(null);
  const [unitRates, setUnitRates] = useState(null);
  const [structureType, setStructureType] = useState('CBS');
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [drillRows, setDrillRows] = useState([]);
  const [drillOpen, setDrillOpen] = useState(false);
  const [drillCode, setDrillCode] = useState('');
  const [filters, setFilters] = useState({
    country: '',
    city: '',
    projectType: '',
    minGfa: '',
    maxGfa: '',
  });
  const [filtersOpen, setFiltersOpen] = useState(false);

  const loadMeta = useCallback(async () => {
    try {
      const [projRes, cmpRes] = await Promise.all([
        projectsApi.list(),
        comparisonsApi.list(),
      ]);
      setProjects(projRes.data || []);
      setComparisons(cmpRes.data || []);
    } catch (e) {
      setError(e.message || 'Failed to load projects/comparisons');
    }
  }, []);

  useEffect(() => {
    loadMeta();
  }, [loadMeta]);

  const activeFilterCount = useMemo(
    () =>
      [filters.country, filters.city, filters.projectType, filters.minGfa, filters.maxGfa].filter(
        (v) => v != null && String(v).trim() !== ''
      ).length,
    [filters]
  );

  const peerCandidates = useMemo(
    () => projects.filter((p) => String(p.id || p.projectId) !== String(selectedProjectId)),
    [projects, selectedProjectId]
  );

  const selectedPeers = useMemo(
    () =>
      peerCandidates.filter((p) =>
        peerIds.map(String).includes(String(p.id || p.projectId))
      ),
    [peerCandidates, peerIds]
  );

  const loadAnalytics = async (id) => {
    const ov = await comparisonsApi.overview(id);
    setOverview(ov.data);
    try {
      const st = await comparisonsApi.structures(id, structureType);
      setStructures(st.data);
    } catch {
      setStructures(null);
    }
    try {
      const ur = await comparisonsApi.unitRates(id);
      setUnitRates(ur.data);
    } catch {
      setUnitRates(null);
    }
  };

  const handleCreate = async () => {
    if (!selectedProjectId) {
      setError('Select a subject project');
      return;
    }
    if (!peerIds.length) {
      setError('Select at least one comparison project');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const res = await comparisonsApi.create({
        comparisonName: comparisonName || 'Comparison',
        selectedProjectId: Number(selectedProjectId),
        peerProjectIds: peerIds.map(Number),
      });
      const id = res.data?.comparisonId || res.data?.id;
      setActiveComparisonId(String(id));
      await comparisonsApi.calculate(id);
      await loadAnalytics(id);
      await loadMeta();
    } catch (e) {
      setError(e.message || 'Failed to create comparison');
    } finally {
      setLoading(false);
    }
  };

  const handleLoadExisting = async (id) => {
    if (!id) {
      setActiveComparisonId('');
      setOverview(null);
      setStructures(null);
      setUnitRates(null);
      setSelectedProjectId('');
      setPeerIds([]);
      return;
    }
    setActiveComparisonId(String(id));
    setLoading(true);
    setError('');
    try {
      // Refill selected + target projects from saved comparison
      try {
        const meta = await comparisonsApi.getById(id);
        const data = meta.data || {};
        if (data.comparisonName) setComparisonName(data.comparisonName);
        if (data.selectedProjectId != null) {
          setSelectedProjectId(String(data.selectedProjectId));
        }
        const peers = (data.peerProjects || [])
          .filter((p) => p.included !== false)
          .map((p) => String(p.projectId));
        setPeerIds(peers);
      } catch {
        // Fall through — overview still loads below
      }
      await loadAnalytics(id);
    } catch (e) {
      setError(e.message || 'Failed to load overview');
      setOverview(null);
    } finally {
      setLoading(false);
    }
  };

  const handleRecalculate = async () => {
    if (!activeComparisonId) return;
    setLoading(true);
    setError('');
    try {
      await comparisonsApi.calculate(activeComparisonId);
      await loadAnalytics(activeComparisonId);
    } catch (e) {
      setError(e.message || 'Calculate failed');
    } finally {
      setLoading(false);
    }
  };

  const handleApplyFilters = async () => {
    if (!activeComparisonId && !selectedProjectId) {
      setError('Create or load a comparison first, or select a subject project');
      return;
    }
    setLoading(true);
    setError('');
    try {
      let id = activeComparisonId;
      if (!id) {
        const res = await comparisonsApi.create({
          comparisonName: comparisonName || 'Filtered comparison',
          selectedProjectId: Number(selectedProjectId),
          peerProjectIds: [],
        });
        id = String(res.data?.comparisonId || res.data?.id);
        setActiveComparisonId(id);
      }
      const payload = {
        country: filters.country || undefined,
        city: filters.city || undefined,
        projectType: filters.projectType || undefined,
        minGfa: filters.minGfa !== '' ? Number(filters.minGfa) : undefined,
        maxGfa: filters.maxGfa !== '' ? Number(filters.maxGfa) : undefined,
        apply: true,
      };
      const res = await comparisonsApi.filterPeers(id, payload);
      const matched = (res.data?.projectIds || []).map(String);
      const matchCount = res.data?.matchCount ?? matched.length;
      const peersPreserved = Boolean(res.data?.peersPreserved);
      if (matched.length > 0) {
        setPeerIds(matched);
      } else if (peersPreserved || matchCount === 0) {
        // Keep current targets — backend no longer wipes on zero matches
        setError(
          matchCount === 0
            ? 'No projects matched these filters. Target projects were left unchanged.'
            : ''
        );
      } else {
        setPeerIds(matched);
      }
      if (matched.length > 0) {
        await comparisonsApi.calculate(id);
        await loadAnalytics(id);
      }
      await loadMeta();
    } catch (e) {
      setError(e.message || 'Filter failed (backend may still be deploying)');
    } finally {
      setLoading(false);
    }
  };

  const openDrill = async (structureCode) => {
    if (!activeComparisonId || !structureCode) return;
    setLoading(true);
    try {
      const res = await comparisonsApi.drillDown(activeComparisonId, structureCode);
      setDrillRows(res.data || []);
      setDrillCode(structureCode);
      setDrillOpen(true);
    } catch (e) {
      setError(e.message || 'Drill-down failed');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!activeComparisonId) return;
    comparisonsApi
      .structures(activeComparisonId, structureType)
      .then((st) => setStructures(st.data))
      .catch(() => setStructures(null));
  }, [structureType, activeComparisonId]);

  const selected = overview?.selected;
  const position = overview?.selectedPosition;
  const allProjects = useMemo(() => {
    if (!selected) return [];
    return [selected, ...(overview?.peers || [])];
  }, [overview, selected]);

  const peerRows = (overview?.peers || []).map((p) => ({ id: p.projectId, ...p }));
  const summaryRows = allProjects.map((p) => ({
    id: p.projectId,
    ...p,
    variancePct:
      p.revisedBudget && Number(p.revisedBudget) !== 0
        ? ((Number(p.actualCost) - Number(p.revisedBudget)) / Number(p.revisedBudget)) * 100
        : null,
  }));

  const avgActual =
    allProjects.length > 0
      ? allProjects.reduce((s, p) => s + Number(p.actualCost || 0), 0) / allProjects.length
      : 0;
  const avgCostM2 =
    allProjects.filter((p) => p.costPerM2 != null).length > 0
      ? allProjects
          .filter((p) => p.costPerM2 != null)
          .reduce((s, p) => s + Number(p.costPerM2), 0) /
        allProjects.filter((p) => p.costPerM2 != null).length
      : null;

  const insights = useMemo(() => {
    if (!selected || !overview) return [];
    const items = [];
    if (position?.deviationVsAveragePct != null) {
      items.push({
        title: 'Cost / m² vs average',
        text: `${pct(position.deviationVsAveragePct)} vs comparison average`,
        tone: position.deviationVsAveragePct,
        accent: '#00897B',
        chip: position.band || 'Typical',
      });
    }
    if (avgActual && selected.actualCost != null) {
      const d = ((Number(selected.actualCost) - avgActual) / avgActual) * 100;
      items.push({
        title: 'Actual cost vs average',
        text: `vs peer-set average ${money(avgActual)}`,
        highlight: pct(d),
        tone: d,
        accent: '#1565C0',
      });
    }
    if ((overview.outliers || []).length) {
      items.push({
        title: 'Outliers',
        text: `${overview.outliers.length} flagged by IQR (shown on Overview).`,
        accent: '#EF6C00',
        chip: 'IQR',
      });
    }
    const topStructure = (structures?.rows || [])[0];
    if (topStructure) {
      const structDev =
        topStructure.deviationVsMedianPct != null
          ? Number(topStructure.deviationVsMedianPct)
          : topStructure.medianActual && Number(topStructure.medianActual) !== 0
            ? ((Number(topStructure.selectedActual) - Number(topStructure.medianActual)) /
                Number(topStructure.medianActual)) *
              100
            : null;
      items.push({
        title: 'Structure sample',
        text: `${topStructure.structureCode} — selected actual ${money(topStructure.selectedActual)}, median ${money(topStructure.medianActual)}`,
        accent: '#8B1538',
        tone: structDev,
        highlight: structDev != null ? pct(structDev) : null,
      });
    }
    if (!items.length) {
      items.push({
        title: 'Ready',
        text: 'Create a comparison to generate insights vs the peer set.',
        accent: '#546E7A',
      });
    }
    return items;
  }, [selected, overview, avgActual, position, structures]);

  const peerColumns = [
    { field: 'projectNumber', headerName: 'Project #', width: 110 },
    { field: 'projectName', headerName: 'Name', flex: 1, minWidth: 160 },
    {
      field: 'originalCost',
      headerName: 'Estimate / BoQ',
      width: 140,
      type: 'number',
      align: 'right',
      headerAlign: 'right',
      valueFormatter: (v) => money(v),
    },
    {
      field: 'changeOrders',
      headerName: 'CO',
      width: 110,
      type: 'number',
      align: 'right',
      headerAlign: 'right',
      valueFormatter: (v) => money(v),
    },
    {
      field: 'revisedBudget',
      headerName: 'Revised',
      width: 130,
      type: 'number',
      align: 'right',
      headerAlign: 'right',
      valueFormatter: (v) => money(v),
    },
    {
      field: 'actualCost',
      headerName: 'Actual',
      width: 130,
      type: 'number',
      align: 'right',
      headerAlign: 'right',
      valueFormatter: (v) => money(v),
    },
    {
      field: 'costPerM2',
      headerName: 'Cost / m²',
      width: 120,
      type: 'number',
      align: 'right',
      headerAlign: 'right',
      valueFormatter: (v) => (v == null ? '—' : money(v)),
    },
    {
      field: 'grossFloorArea',
      headerName: 'GFA',
      width: 100,
      type: 'number',
      align: 'right',
      headerAlign: 'right',
      valueFormatter: (v) => num(v, 0),
    },
  ];

  const summaryColumns = [
    { field: 'projectNumber', headerName: 'ID', width: 100 },
    { field: 'projectName', headerName: 'Name', flex: 1, minWidth: 140 },
    {
      field: 'grossFloorArea',
      headerName: 'GFA',
      width: 100,
      type: 'number',
      align: 'right',
      headerAlign: 'right',
      valueFormatter: (v) => num(v, 0),
    },
    {
      field: 'originalCost',
      headerName: 'Estimate / BoQ',
      width: 140,
      type: 'number',
      align: 'right',
      headerAlign: 'right',
      valueFormatter: (v) => money(v),
    },
    {
      field: 'actualCost',
      headerName: 'Actual',
      width: 130,
      type: 'number',
      align: 'right',
      headerAlign: 'right',
      valueFormatter: (v) => money(v),
    },
    {
      field: 'variancePct',
      headerName: 'Var %',
      width: 90,
      type: 'number',
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => (
        <Box component="span" sx={varianceSx(params.value)}>
          {pct(params.value)}
        </Box>
      ),
    },
    {
      field: 'costPerM2',
      headerName: 'Cost/m²',
      width: 120,
      type: 'number',
      align: 'right',
      headerAlign: 'right',
      valueFormatter: (v) => (v == null ? '—' : money(v)),
    },
  ];

  const structureColumns = [
    { field: 'structureCode', headerName: 'Code', width: 120 },
    { field: 'structureName', headerName: 'Name', flex: 1, minWidth: 140 },
    {
      field: 'selectedActual',
      headerName: 'Selected Actual',
      width: 140,
      type: 'number',
      align: 'right',
      headerAlign: 'right',
      valueFormatter: (v) => money(v),
    },
    {
      field: 'medianActual',
      headerName: 'Median',
      width: 130,
      type: 'number',
      align: 'right',
      headerAlign: 'right',
      valueFormatter: (v) => money(v),
    },
    {
      field: 'p25Actual',
      headerName: 'P25',
      width: 120,
      type: 'number',
      align: 'right',
      headerAlign: 'right',
      valueFormatter: (v) => money(v),
    },
    {
      field: 'p75Actual',
      headerName: 'P75',
      width: 120,
      type: 'number',
      align: 'right',
      headerAlign: 'right',
      valueFormatter: (v) => money(v),
    },
    {
      field: 'deviationVsMedianPct',
      headerName: 'Dev vs Med',
      width: 110,
      type: 'number',
      align: 'right',
      headerAlign: 'right',
      valueFormatter: (v) => pct(v),
    },
  ];

  const unitColumns = [
    { field: 'key', headerName: 'Key', width: 120 },
    { field: 'description', headerName: 'Description', flex: 1, minWidth: 160 },
    { field: 'unit', headerName: 'Unit', width: 70 },
    {
      field: 'selectedEstimateRate',
      headerName: 'Est rate',
      width: 110,
      type: 'number',
      align: 'right',
      headerAlign: 'right',
      valueFormatter: (v) => num(v),
    },
    {
      field: 'selectedActualRate',
      headerName: 'Act rate',
      width: 110,
      type: 'number',
      align: 'right',
      headerAlign: 'right',
      valueFormatter: (v) => num(v),
    },
    {
      field: 'medianEstimateRate',
      headerName: 'Med est',
      width: 110,
      type: 'number',
      align: 'right',
      headerAlign: 'right',
      valueFormatter: (v) => num(v),
    },
    {
      field: 'varianceVsMedianPct',
      headerName: 'Var %',
      width: 90,
      type: 'number',
      align: 'right',
      headerAlign: 'right',
      valueFormatter: (v) => pct(v),
    },
  ];

  const statRows = (overview?.statistics || []).map((s, idx) => ({
    id: s.metricCode || idx,
    ...s,
  }));

  const tintCell = (bg, color) => (params) => (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        px: 0.5,
        bgcolor: bg,
        color,
        fontWeight: 600,
        borderRadius: 0.5,
      }}
    >
      {num(params.value)}
    </Box>
  );

  const statColumns = [
    { field: 'metricCode', headerName: 'Metric', width: 150 },
    { field: 'sampleCount', headerName: 'N', width: 60 },
    {
      field: 'min',
      headerName: 'Min',
      width: 100,
      renderCell: tintCell('rgba(21, 101, 192, 0.08)', '#1565C0'),
    },
    { field: 'p25', headerName: 'P25', width: 100, valueFormatter: (v) => num(v) },
    {
      field: 'median',
      headerName: 'Median',
      width: 100,
      renderCell: tintCell('rgba(46, 125, 50, 0.10)', '#2E7D32'),
    },
    { field: 'average', headerName: 'Avg', width: 100, valueFormatter: (v) => num(v) },
    { field: 'p75', headerName: 'P75', width: 100, valueFormatter: (v) => num(v) },
    {
      field: 'max',
      headerName: 'Max',
      width: 100,
      renderCell: tintCell('rgba(239, 108, 0, 0.10)', '#E65100'),
    },
  ];

  const maxBar = Math.max(...allProjects.map((p) => Number(p.actualCost || 0)), 1);
  const maxM2 = Math.max(...allProjects.map((p) => Number(p.costPerM2 || 0)), 1);

  return (
    <RootDiv>
      <ContentDiv>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
          <CompareArrows color="primary" />
          <Typography variant="h6" fontWeight={600}>
            Project Comparison
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Hotel-style benchmark · select project vs peers
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <Paper sx={{ p: 2, mb: 1.5 }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5 }}>
            <TextField
              size="small"
              label="Comparison name"
              value={comparisonName}
              onChange={(e) => setComparisonName(e.target.value)}
              sx={{ flex: '1 1 160px', minWidth: 160, maxWidth: 240 }}
            />
            <FormControl size="small" sx={{ flex: '1 1 220px', minWidth: 200, maxWidth: 320 }}>
              <InputLabel>1. Selected project</InputLabel>
              <Select
                label="1. Selected project"
                value={selectedProjectId}
                onChange={(e) => {
                  setSelectedProjectId(e.target.value);
                  setPeerIds([]);
                }}
              >
                {projects.map((p) => (
                  <MenuItem key={p.id || p.projectId} value={p.id || p.projectId}>
                    {projectLabel(p)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ flex: '1 1 180px', minWidth: 160, maxWidth: 260 }}>
              <InputLabel>Load saved</InputLabel>
              <Select
                label="Load saved"
                value={activeComparisonId}
                onChange={(e) => handleLoadExisting(e.target.value)}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {comparisons.map((c) => (
                  <MenuItem key={c.comparisonId} value={String(c.comparisonId)}>
                    {c.comparisonName} (#{c.comparisonId})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Box sx={{ display: 'flex', gap: 1, flexShrink: 0, ml: { md: 'auto' } }}>
              <Button variant="contained" onClick={handleCreate} disabled={loading}>
                Create & compare
              </Button>
              <Button
                variant="outlined"
                onClick={handleRecalculate}
                disabled={loading || !activeComparisonId}
              >
                Recalculate
              </Button>
            </Box>
          </Box>

          <Typography variant="subtitle2" sx={{ mt: 2, mb: 0.5, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            2. Target projects (multi-select)
            {selectedProjectId && (
              <Chip
                size="small"
                label={`Source: ${
                  projectLabel(
                    projects.find((p) => String(p.id || p.projectId) === String(selectedProjectId))
                  ) || selectedProjectId
                }`}
                sx={{
                  bgcolor: 'rgba(139, 21, 56, 0.10)',
                  color: '#8B1538',
                  border: '1px solid rgba(139, 21, 56, 0.35)',
                  fontWeight: 600,
                  height: 22,
                }}
              />
            )}
          </Typography>
          <Autocomplete
            multiple
            size="small"
            options={peerCandidates}
            value={selectedPeers}
            disableCloseOnSelect
            getOptionDisabled={(option) =>
              String(option.id || option.projectId) === String(selectedProjectId)
            }
            getOptionLabel={(p) => projectLabel(p)}
            isOptionEqualToValue={(a, b) =>
              String(a.id || a.projectId) === String(b.id || b.projectId)
            }
            onChange={(_, value) => {
              const next = value
                .map((p) => p.id || p.projectId)
                .filter((id) => String(id) !== String(selectedProjectId));
              setPeerIds(next);
            }}
            noOptionsText={
              selectedProjectId
                ? 'No other projects (source is excluded from targets)'
                : 'Select a source project first'
            }
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  {...getTagProps({ index })}
                  size="small"
                  key={option.id || option.projectId}
                  label={projectLabel(option)}
                  sx={{
                    bgcolor: 'rgba(0, 137, 123, 0.12)',
                    color: '#00695C',
                    border: '1px solid rgba(0, 137, 123, 0.35)',
                    fontWeight: 600,
                    '& .MuiChip-deleteIcon': { color: '#00897B' },
                  }}
                />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Target projects"
                placeholder={selectedPeers.length ? '' : 'Select projects to compare against'}
                helperText="Selected (source) project cannot also be a target"
              />
            )}
            sx={{ maxWidth: 720 }}
          />

          <Box
            sx={{
              mt: 1.5,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              flexWrap: 'wrap',
            }}
          >
            <Button
              size="small"
              startIcon={<FilterList />}
              endIcon={filtersOpen ? <ExpandLess /> : <ExpandMore />}
              onClick={() => setFiltersOpen((o) => !o)}
              sx={{ textTransform: 'none', fontWeight: 600, color: 'text.secondary' }}
            >
              Filters (optional)
            </Button>
            {activeFilterCount > 0 && (
              <Chip
                size="small"
                color="primary"
                variant="outlined"
                label={`${activeFilterCount} active`}
                onClick={() => setFiltersOpen(true)}
              />
            )}
          </Box>
          <Collapse in={filtersOpen}>
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 1,
                alignItems: 'center',
                mt: 1,
                p: 1.25,
                borderRadius: 1,
                bgcolor: 'rgba(0, 0, 0, 0.02)',
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <TextField
                size="small"
                label="Country"
                value={filters.country}
                onChange={(e) => setFilters((f) => ({ ...f, country: e.target.value }))}
                sx={{ width: 120, '& .MuiOutlinedInput-root': { height: 40 } }}
              />
              <TextField
                size="small"
                label="City"
                value={filters.city}
                onChange={(e) => setFilters((f) => ({ ...f, city: e.target.value }))}
                sx={{ width: 120, '& .MuiOutlinedInput-root': { height: 40 } }}
              />
              <TextField
                size="small"
                label="Type"
                value={filters.projectType}
                onChange={(e) => setFilters((f) => ({ ...f, projectType: e.target.value }))}
                sx={{ width: 120, '& .MuiOutlinedInput-root': { height: 40 } }}
              />
              <TextField
                size="small"
                label="Min GFA"
                value={filters.minGfa}
                onChange={(e) => setFilters((f) => ({ ...f, minGfa: e.target.value }))}
                sx={{ width: 100, '& .MuiOutlinedInput-root': { height: 40 } }}
              />
              <TextField
                size="small"
                label="Max GFA"
                value={filters.maxGfa}
                onChange={(e) => setFilters((f) => ({ ...f, maxGfa: e.target.value }))}
                sx={{ width: 100, '& .MuiOutlinedInput-root': { height: 40 } }}
              />
              <Button
                size="medium"
                variant="outlined"
                onClick={handleApplyFilters}
                disabled={loading}
                sx={{
                  height: 40,
                  minHeight: 40,
                  py: 0,
                  px: 2,
                  flexShrink: 0,
                  ml: { xs: 0, sm: 'auto' },
                }}
              >
                Apply filters
              </Button>
            </Box>
          </Collapse>
        </Paper>

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={28} />
          </Box>
        )}

        {selected && (
          <>
            <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
              {[
                {
                  label: 'Selected',
                  value: projectLabel(selected) || selected.projectNumber,
                  sub: selected.projectNumber || null,
                },
                {
                  label: 'Actual',
                  value: money(selected.actualCost),
                  sub:
                    avgActual > 0
                      ? pct(((Number(selected.actualCost) - avgActual) / avgActual) * 100)
                      : null,
                  subTone:
                    avgActual > 0
                      ? ((Number(selected.actualCost) - avgActual) / avgActual) * 100
                      : null,
                },
                {
                  label: 'Cost / m²',
                  value: selected.costPerM2 == null ? '—' : money(selected.costPerM2),
                  sub:
                    position?.deviationVsAveragePct != null
                      ? pct(position.deviationVsAveragePct)
                      : avgCostM2 != null && selected.costPerM2 != null
                        ? pct(((Number(selected.costPerM2) - avgCostM2) / avgCostM2) * 100)
                        : null,
                  subTone:
                    position?.deviationVsAveragePct != null
                      ? position.deviationVsAveragePct
                      : avgCostM2 != null && selected.costPerM2 != null
                        ? ((Number(selected.costPerM2) - avgCostM2) / avgCostM2) * 100
                        : null,
                },
                {
                  label: 'Percentile',
                  value: position?.percentileRank != null ? `${Math.round(position.percentileRank)}th` : '—',
                  sub: position?.band || '—',
                  chip: position?.band,
                  tip: 'Where this project’s Cost / m² ranks among the comparison set. 50th = median (Typical). Lower / Higher bands mean cheaper or more expensive than peers.',
                },
                { label: 'Peers', value: String(overview?.peerCount ?? peerRows.length), sub: 'in set' },
                {
                  label: 'Outliers',
                  value: String((overview?.outliers || []).length),
                  sub: 'IQR flags',
                },
              ].map((card) => {
                const accent = KPI_ACCENTS[card.label] || '#8B1538';
                const body = (
                  <Card
                    variant="outlined"
                    sx={{
                      height: '100%',
                      borderLeft: `3px solid ${accent}`,
                      bgcolor: `${accent}08`,
                      cursor: card.tip ? 'help' : 'default',
                    }}
                  >
                    <CardContent sx={{ py: 1.25, '&:last-child': { pb: 1.25 } }}>
                      <Typography variant="caption" sx={{ color: accent, fontWeight: 600 }}>
                        {card.label}
                      </Typography>
                      <Typography variant="subtitle1" fontWeight={600} noWrap title={card.value}>
                        {card.value}
                      </Typography>
                      {card.chip ? (
                        <Chip size="small" label={card.chip} color={bandColor(card.chip)} sx={{ mt: 0.5 }} />
                      ) : (
                        card.sub && (
                          <Typography
                            variant="caption"
                            sx={
                              card.subTone != null
                                ? varianceSx(card.subTone)
                                : { color: 'text.secondary' }
                            }
                          >
                            {card.sub}
                          </Typography>
                        )
                      )}
                    </CardContent>
                  </Card>
                );
                return (
                  <Grid item xs={6} sm={4} md={2} key={card.label}>
                    {card.tip ? <Tooltip title={card.tip}>{body}</Tooltip> : body}
                  </Grid>
                );
              })}
            </Grid>

            <Paper sx={{ mb: 1.5 }}>
              <StyledTabs
                value={tab}
                onChange={(_, v) => setTab(v)}
                variant="scrollable"
                scrollButtons="auto"
              >
                <StyledTab label="Overview" />
                <StyledTab label="Cost" />
                <StyledTab label="Cost / m²" />
                <StyledTab label="Structures" />
                <StyledTab label="Comparison" />
                <StyledTab label="Unit Rates" />
                <StyledTab label="Schedule" />
                <StyledTab label="Insights" />
              </StyledTabs>
              <Box sx={{ p: 2 }}>
                {tab === 0 && (
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={8}>
                      <Typography variant="subtitle2" gutterBottom>
                        Project comparison summary
                      </Typography>
                      <Box sx={{ height: 280 }}>
                        <DataGrid
                          rows={summaryRows}
                          columns={summaryColumns}
                          density="compact"
                          hideFooter
                          disableRowSelectionOnClick
                          getRowClassName={(params) =>
                            selected && String(params.id) === String(selected.projectId)
                              ? 'row-source-project'
                              : 'row-peer-project'
                          }
                          sx={{
                            border: 'none',
                            '& .row-source-project': {
                              bgcolor: 'rgba(139, 21, 56, 0.06)',
                              '&:hover': { bgcolor: 'rgba(139, 21, 56, 0.10)' },
                            },
                            '& .row-peer-project': {
                              bgcolor: 'rgba(0, 137, 123, 0.03)',
                            },
                          }}
                        />
                      </Box>
                      {(overview?.outliers || []).length > 0 && (
                        <Alert severity="warning" sx={{ mt: 1 }}>
                          Outliers:{' '}
                          {(overview.outliers || [])
                            .map((o) => `${projectLabel(o) || o.projectNumber || o.projectId} (${o.metricCode} ${o.direction})`)
                            .join(', ')}
                        </Alert>
                      )}
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Typography variant="subtitle2" gutterBottom>
                        Peer set statistics
                      </Typography>
                      <Box sx={{ height: 280 }}>
                        <DataGrid
                          rows={statRows}
                          columns={statColumns}
                          density="compact"
                          hideFooter
                          disableRowSelectionOnClick
                          sx={{
                            border: 'none',
                            '& .MuiDataGrid-columnHeader[data-field="min"], & .MuiDataGrid-columnHeader[data-field="max"], & .MuiDataGrid-columnHeader[data-field="median"]':
                              {
                                color: 'text.primary',
                                fontWeight: 700,
                              },
                          }}
                        />
                      </Box>
                    </Grid>
                  </Grid>
                )}

                {tab === 1 && (
                  <Box>
                    <Typography variant="subtitle2" gutterBottom>
                      Total project cost comparison
                    </Typography>
                    {allProjects.map((p) => (
                      <Box key={p.projectId} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
                        <Typography variant="caption" sx={{ width: 140 }} noWrap title={projectLabel(p)}>
                          {projectLabel(p)}
                        </Typography>
                        <Box sx={{ flex: 1, bgcolor: 'grey.100', borderRadius: 1, height: 18 }}>
                          <Box
                            sx={{
                              width: `${(Number(p.actualCost || 0) / maxBar) * 100}%`,
                              height: '100%',
                              bgcolor:
                                String(p.projectId) === String(selected.projectId)
                                  ? 'primary.main'
                                  : '#00897B',
                              borderRadius: 1,
                            }}
                          />
                        </Box>
                        <Typography variant="caption" sx={{ width: 90, textAlign: 'right' }}>
                          {money(p.actualCost)}
                        </Typography>
                      </Box>
                    ))}
                    <Box sx={{ height: 240, mt: 2 }}>
                      <DataGrid
                        rows={peerRows}
                        columns={peerColumns}
                        density="compact"
                        hideFooter
                        disableRowSelectionOnClick
                        sx={{ border: 'none' }}
                      />
                    </Box>
                  </Box>
                )}

                {tab === 2 && (
                  <Box>
                    <Typography variant="subtitle2" gutterBottom>
                      Cost / m² comparison
                    </Typography>
                    {allProjects.map((p) => (
                      <Box key={p.projectId} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
                        <Typography variant="caption" sx={{ width: 140 }} noWrap title={projectLabel(p)}>
                          {projectLabel(p)}
                        </Typography>
                        <Box sx={{ flex: 1, bgcolor: 'grey.100', borderRadius: 1, height: 18 }}>
                          <Box
                            sx={{
                              width: `${(Number(p.costPerM2 || 0) / maxM2) * 100}%`,
                              height: '100%',
                              bgcolor:
                                String(p.projectId) === String(selected.projectId)
                                  ? 'primary.main'
                                  : '#00897B',
                              borderRadius: 1,
                            }}
                          />
                        </Box>
                        <Typography variant="caption" sx={{ width: 90, textAlign: 'right' }}>
                          {p.costPerM2 == null ? '—' : money(p.costPerM2)}
                        </Typography>
                      </Box>
                    ))}
                    {position && (
                      <Alert severity="info" sx={{ mt: 2 }}>
                        Selected Cost/m² is at the{' '}
                        <strong>
                          {position.percentileRank != null
                            ? `${Math.round(position.percentileRank)}th`
                            : '—'}{' '}
                          percentile
                        </strong>{' '}
                        · {pct(position.deviationVsMedianPct)} vs median · band{' '}
                        <Chip size="small" label={position.band || 'Typical'} color={bandColor(position.band)} />
                      </Alert>
                    )}
                  </Box>
                )}

                {tab === 3 && (
                  <Box>
                    <Box sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
                      <FormControl size="small" sx={{ minWidth: 180 }}>
                        <InputLabel>Structure type</InputLabel>
                        <Select
                          label="Structure type"
                          value={structureType}
                          onChange={(e) => setStructureType(e.target.value)}
                        >
                          {['CBS', 'WBS', 'MASTERFORMAT', 'UNIFORMAT', 'POMI'].map((t) => (
                            <MenuItem key={t} value={t}>
                              {t}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <Typography variant="caption" color="text.secondary">
                        Click a row to drill into BoQ / transactions
                      </Typography>
                    </Box>
                    {!structures ? (
                      <Alert severity="info">
                        Structure API not available yet, or no matching structure codes across peers.
                      </Alert>
                    ) : (
                      <Box sx={{ height: 360 }}>
                        <DataGrid
                          rows={(structures.rows || []).map((r, i) => ({
                            id: r.structureCode || i,
                            ...r,
                          }))}
                          columns={structureColumns}
                          density="compact"
                          pageSizeOptions={[10, 25]}
                          initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
                          onRowClick={(params) => openDrill(params.row.structureCode)}
                          sx={{ border: 'none', cursor: 'pointer' }}
                        />
                      </Box>
                    )}
                  </Box>
                )}

                {tab === 4 && (
                  <BenchmarkComparisonGridTab comparisonId={activeComparisonId} />
                )}

                {tab === 5 && (
                  <Box>
                    {!unitRates ? (
                      <Alert severity="info">
                        Unit-rate API not available yet, or no matching BoQ items across peers.
                      </Alert>
                    ) : (
                      <Box sx={{ height: 360 }}>
                        <DataGrid
                          rows={(unitRates.rows || []).map((r, i) => ({
                            id: r.key || i,
                            ...r,
                          }))}
                          columns={unitColumns}
                          density="compact"
                          pageSizeOptions={[10, 25]}
                          initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
                          sx={{ border: 'none' }}
                        />
                      </Box>
                    )}
                  </Box>
                )}

                {tab === 6 && (
                  <Alert severity="info">
                    Schedule comparison is not set up yet. It will use Planned / Actual duration from
                    project fields when those are available.
                  </Alert>
                )}

                {tab === 7 && (
                  <Grid container spacing={1.5}>
                    {insights.map((ins) => (
                      <Grid item xs={12} md={6} key={ins.title}>
                        <Card
                          variant="outlined"
                          sx={{
                            height: '100%',
                            borderLeft: `4px solid ${ins.accent || '#8B1538'}`,
                            bgcolor: `${ins.accent || '#8B1538'}0A`,
                          }}
                        >
                          <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: 1,
                                mb: 0.5,
                              }}
                            >
                              <Typography
                                variant="subtitle2"
                                fontWeight={700}
                                sx={{ color: ins.accent || '#8B1538' }}
                              >
                                {ins.title}
                              </Typography>
                              {ins.chip && (
                                <Chip
                                  size="small"
                                  label={ins.chip}
                                  color={bandColor(ins.chip)}
                                  sx={{ height: 22 }}
                                />
                              )}
                            </Box>
                            {ins.highlight && (
                              <Typography variant="h6" sx={{ ...varianceSx(ins.tone), mb: 0.25 }}>
                                {ins.highlight}
                              </Typography>
                            )}
                            {!ins.highlight && ins.tone != null && (
                              <Typography variant="body2" sx={{ ...varianceSx(ins.tone), mb: 0.25 }}>
                                {pct(ins.tone)}
                              </Typography>
                            )}
                            <Typography variant="body2" color="text.secondary">
                              {ins.text}
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                )}
              </Box>
            </Paper>
          </>
        )}
      </ContentDiv>

      <Dialog open={drillOpen} onClose={() => setDrillOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Drill-down · {drillCode}</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ height: 360 }}>
            <DataGrid
              rows={(drillRows || []).map((r, i) => ({
                id: r.costTransactionId || r.id || i,
                ...r,
              }))}
              columns={[
                { field: 'transactionType', headerName: 'Type', width: 110 },
                { field: 'boqItemNumber', headerName: 'BoQ', width: 100 },
                { field: 'description', headerName: 'Description', flex: 1, minWidth: 160 },
                { field: 'quantity', headerName: 'Qty', width: 90, type: 'number', align: 'right', headerAlign: 'right', valueFormatter: (v) => num(v) },
                { field: 'unit', headerName: 'Unit', width: 70 },
                { field: 'unitPrice', headerName: 'Rate', width: 110, type: 'number', align: 'right', headerAlign: 'right', valueFormatter: (v) => num(v) },
                { field: 'amount', headerName: 'Amount', width: 130, type: 'number', align: 'right', headerAlign: 'right', valueFormatter: (v) => money(v) },
              ]}
              density="compact"
              sx={{ border: 'none' }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDrillOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </RootDiv>
  );
};

export default BenchmarkProjectComparison;
